import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Plus, Copy, Edit3, Send, Archive, Lock, Save, Trash2, RotateCcw } from 'lucide-react';

// =======================================================================
// Construtor de VERSÕES da Avaliação de Segurança do Alimento (SA) e da
// Avaliação de Treinamento (TSV) — DEM-00014.
// -----------------------------------------------------------------------
// UM componente para as DUAS gêmeas (o backend também é uma factory por
// FORMULARIO): quem escolhe o formulário é a prop `apiBase`
// ('/api/operacional/avaliacaosa/versoes' para a SA, '/api/avaliacao/versoes'
// para a TSV) e a `cor` do módulo (SA ardósia #475569, TSV azul #0284c7).
//
// Espelha o ChecklistVersoes (o molde mais próximo), trocando o que é próprio
// da SA/TSV:
//   - CORTE_VETO por VERSÃO (não "método"): nota abaixo dele reprova; vazio =
//     sem corte por nota (o caso da TSV). Só editável enquanto a versão não foi
//     usada (mesma trava do método no Checklist).
//   - PONTUA e CRÍTICA por PERGUNTA (não peso/N-A): "Pontua" entra no
//     denominador SIM÷(SIM+NÃO); "Crítica" veta (IMPUR) se respondida "Não".
//   - CÓDIGO da pergunta é preservado (identidade entre versões) — vem do
//     servidor e volta no PUT; pergunta nova nasce sem código e o servidor gera.
// =======================================================================

const statusStyle = (s) => {
  if (s === 'Publicado') return { bg: '#d1fae5', color: '#065f46' };
  if (s === 'Rascunho')  return { bg: '#fef3c7', color: '#92400e' };
  return { bg: '#e2e8f0', color: '#475569' }; // Arquivado
};

const btn = (bg, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 10px', background: bg, color, border: 'none',
  borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer',
});

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const FormularioVersoes = ({ apiBase, cor = '#475569', titulo = 'Gerenciar Versões', onVoltar }) => {
  const [versoes, setVersoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [editando, setEditando] = useState(null); // ID em edição
  const [mostrarNova, setMostrarNova] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(apiBase, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setVersoes(await r.json());
    } catch (e) {
      setErro('Erro ao carregar versões.');
    } finally {
      setCarregando(false);
    }
  }, [apiBase]);

  useEffect(() => { carregar(); }, [carregar]);

  const novaVersao = async (nome) => {
    if (!nome?.trim()) return;
    const r = await fetch(apiBase, {
      method: 'POST',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ nome_versao: nome }),
    });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao criar.'); return; }
    setMostrarNova(false);
    await carregar();
    setEditando(data.id);
  };

  const duplicar = async (id) => {
    const nome = window.prompt('Nome da nova versão (cópia):');
    if (!nome?.trim()) return;
    const r = await fetch(`${apiBase}/${id}/duplicar`, {
      method: 'POST',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ nome_versao: nome }),
    });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao duplicar.'); return; }
    await carregar();
    setEditando(data.id);
  };

  const acao = async (id, verbo, confirmacao) => {
    if (confirmacao && !window.confirm(confirmacao)) return;
    const r = await fetch(`${apiBase}/${id}/${verbo}`, { method: 'POST', headers: auth() });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || `Erro ao ${verbo}.`); return; }
    carregar();
  };

  if (editando) {
    return <VersaoEditor apiBase={apiBase} cor={cor} id={editando} onSair={() => { setEditando(null); carregar(); }} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        {onVoltar && (
          <button onClick={onVoltar} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
            borderRadius: 8, fontWeight: 700, cursor: 'pointer',
          }}>
            <ArrowLeft size={16} /> Voltar
          </button>
        )}
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{titulo}</h3>
      </div>

      <button
        onClick={() => setMostrarNova(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '10px 16px', background: cor, color: '#fff',
          border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', marginBottom: 16,
        }}
      >
        <Plus size={16} /> Nova versão (em branco)
      </button>

      {mostrarNova && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <input
            type="text" autoFocus placeholder="Nome da nova versão"
            onKeyDown={(e) => { if (e.key === 'Enter') novaVersao(e.target.value); if (e.key === 'Escape') setMostrarNova(false); }}
            style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6 }}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
            Pressione Enter para criar — a versão começa como Rascunho.
          </div>
        </div>
      )}

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}
      {carregando && <p style={{ color: '#64748b' }}>Carregando…</p>}

      <div style={{ display: 'grid', gap: 10 }}>
        {versoes.map((v) => {
          const st = statusStyle(v.STATUS);
          const imutavel = v.STATUS === 'Publicado' && v.USOS > 0;
          const corte = v.CORTE_VETO === null || v.CORTE_VETO === undefined
            ? 'Sem corte por nota'
            : `Corte de veto: ${Number(v.CORTE_VETO)}%`;
          return (
            <div key={v.ID} style={{
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16,
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>{v.NOME_VERSAO}</span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 800,
                    background: st.bg, color: st.color,
                  }}>{v.STATUS}</span>
                  {imutavel && (
                    <span title="Versão imutável: já tem avaliações gravadas" style={{ color: '#64748b' }}>
                      <Lock size={14} />
                    </span>
                  )}
                </div>
                {v.DESCRICAO && <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{v.DESCRICAO}</div>}
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  {corte} · Perguntas: {v.TOTAL_PERGUNTAS} · Usos: {v.USOS}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setEditando(v.ID)} title={imutavel ? 'Visualizar (imutável)' : 'Editar'}
                  style={btn('#f1f5f9', '#475569')}>
                  <Edit3 size={14} /> {imutavel ? 'Ver' : 'Editar'}
                </button>
                <button onClick={() => duplicar(v.ID)} title="Duplicar" style={btn('#eff6ff', '#1d4ed8')}>
                  <Copy size={14} /> Duplicar
                </button>
                {v.STATUS === 'Rascunho' && (
                  <button onClick={() => acao(v.ID, 'publicar', 'Publicar esta versão? Após o primeiro uso ela ficará imutável.')}
                    title="Publicar" style={btn('#d1fae5', '#065f46')}>
                    <Send size={14} /> Publicar
                  </button>
                )}
                {v.STATUS === 'Publicado' && (
                  <button onClick={() => acao(v.ID, 'arquivar', 'Arquivar esta versão? Ela some das listas mas as avaliações já feitas continuam preservadas.')}
                    title="Arquivar" style={btn('#f1f5f9', '#475569')}>
                    <Archive size={14} /> Arquivar
                  </button>
                )}
                {v.STATUS === 'Arquivado' && (
                  <button onClick={() => acao(v.ID, 'republicar', 'Republicar esta versão? Ela volta a aparecer para preenchimento.')}
                    title="Republicar" style={btn('#d1fae5', '#065f46')}>
                    <RotateCcw size={14} /> Republicar
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {!carregando && versoes.length === 0 && !erro && (
          <p style={{ color: '#64748b' }}>Nenhuma versão ainda. Crie a primeira em "Nova versão".</p>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Editor da versão (mesmo arquivo para reduzir fragmentação)
// ---------------------------------------------------------------------
const VersaoEditor = ({ apiBase, cor, id, onSair }) => {
  const [dados, setDados] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const r = await fetch(`${apiBase}/${id}`, { headers: auth() });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro ao carregar.');
      // Normaliza para o estado local (lowercase). corte_veto vira '' quando
      // nulo, para o input numérico ficar vazio (= sem corte).
      setDados({
        nome_versao: data.versao.NOME_VERSAO,
        descricao: data.versao.DESCRICAO || '',
        corte_veto: data.versao.CORTE_VETO === null || data.versao.CORTE_VETO === undefined
          ? '' : Number(data.versao.CORTE_VETO),
        status: data.versao.STATUS,
        podeEditar: data.podeEditar,
        usos: data.usos,
        areas: (data.areas || []).map((a) => ({
          id: a.ID, codigo: a.CODIGO, nome: a.NOME, ordem: a.ORDEM,
          perguntas: (a.perguntas || []).map((p) => ({
            id: p.ID, codigo: p.CODIGO, texto: p.TEXTO, ordem: p.ORDEM,
            pontuada: !!p.PONTUADA, e_critica: !!p.E_CRITICA,
          })),
        })),
      });
    } catch (e) {
      setErro(e.message);
    }
  }, [apiBase, id]);

  useEffect(() => { carregar(); }, [carregar]);

  if (!dados) return <p style={{ color: '#64748b' }}>Carregando…</p>;

  const ro = !dados.podeEditar;

  const atualizarArea = (i, campo, valor) => {
    setDados((d) => {
      const novas = [...d.areas];
      novas[i] = { ...novas[i], [campo]: valor };
      return { ...d, areas: novas };
    });
  };
  const adicionarArea = () => {
    setDados((d) => ({ ...d, areas: [...d.areas, { nome: 'Nova Área', ordem: d.areas.length + 1, perguntas: [] }] }));
  };
  const removerArea = (i) => {
    setDados((d) => ({ ...d, areas: d.areas.filter((_, idx) => idx !== i) }));
  };
  const adicionarPergunta = (i) => {
    setDados((d) => {
      const novas = [...d.areas];
      // Pergunta nova nasce pontuada e não crítica (o caso comum); sem código —
      // o servidor gera um token de identidade ao salvar.
      novas[i] = {
        ...novas[i],
        perguntas: [...novas[i].perguntas, { texto: '', ordem: novas[i].perguntas.length + 1, pontuada: true, e_critica: false }],
      };
      return { ...d, areas: novas };
    });
  };
  const atualizarPergunta = (ai, pi, campo, valor) => {
    setDados((d) => {
      const novas = [...d.areas];
      const ps = [...novas[ai].perguntas];
      ps[pi] = { ...ps[pi], [campo]: valor };
      novas[ai] = { ...novas[ai], perguntas: ps };
      return { ...d, areas: novas };
    });
  };
  const removerPergunta = (ai, pi) => {
    setDados((d) => {
      const novas = [...d.areas];
      novas[ai] = { ...novas[ai], perguntas: novas[ai].perguntas.filter((_, i) => i !== pi) };
      return { ...d, areas: novas };
    });
  };

  const salvar = async () => {
    setSalvando(true); setErro(''); setSucesso('');
    try {
      const r = await fetch(`${apiBase}/${id}`, {
        method: 'PUT',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome_versao: dados.nome_versao,
          descricao: dados.descricao,
          // '' → null (sem corte por nota). O backend distingue "não mandou" de
          // "mandou null" para permitir zerar o corte de propósito.
          corte_veto: dados.corte_veto === '' ? null : Number(dados.corte_veto),
          areas: dados.areas.map((a, ai) => ({
            // Preserva o código (identidade entre versões); área nova vai sem, e
            // o servidor gera.
            codigo: a.codigo || undefined,
            nome: a.nome, ordem: ai + 1,
            perguntas: a.perguntas.map((p, pi) => ({
              codigo: p.codigo || undefined,
              texto: p.texto, ordem: pi + 1,
              pontuada: p.pontuada ? 1 : 0, e_critica: p.e_critica ? 1 : 0,
            })),
          })),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro ao salvar.');
      setSucesso('Versão salva.');
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const totalPerguntas = dados.areas.reduce((acc, a) => acc + a.perguntas.length, 0);
  const totalPontuadas = dados.areas.reduce((acc, a) => acc + a.perguntas.filter((p) => p.pontuada).length, 0);
  const totalCriticas = dados.areas.reduce((acc, a) => acc + a.perguntas.filter((p) => p.e_critica).length, 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onSair} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
          borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        }}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Editor: {dados.nome_versao}
        </h3>
      </div>

      {ro && (
        <div style={{
          padding: 12, background: '#fef3c7', color: '#92400e', borderRadius: 8, marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Lock size={16} /> Versão imutável: já tem {dados.usos} avaliação(ões) gravada(s). Para modificar, use "Duplicar".
        </div>
      )}
      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}
      {sucesso && <div style={{ padding: 12, background: '#d1fae5', color: '#065f46', borderRadius: 8, marginBottom: 12 }}>{sucesso}</div>}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Nome da versão</label>
            <input type="text" disabled={ro} value={dados.nome_versao}
              onChange={(e) => setDados({ ...dados, nome_versao: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Descrição</label>
            <input type="text" disabled={ro} value={dados.descricao}
              onChange={(e) => setDados({ ...dados, descricao: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
          </div>
          <div>
            {/* O corte de veto viaja na VERSÃO — só é trocável aqui porque `ro`
                (podeEditar) já garante ZERO avaliações usando esta versão.
                Vazio = sem corte por nota (o caso da TSV). */}
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Corte de veto (%)</label>
            <input type="number" min={0} max={100} disabled={ro} value={dados.corte_veto} placeholder="sem corte"
              onChange={(e) => setDados({ ...dados, corte_veto: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Vazio = sem corte por nota.</div>
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 13, color: '#475569' }}>
          Status: <strong>{dados.status}</strong> · Perguntas: <strong>{totalPerguntas}</strong>{' '}
          (pontuadas <strong>{totalPontuadas}</strong> · críticas <strong>{totalCriticas}</strong>) ·
          Áreas: <strong>{dados.areas.length}</strong>
        </div>
      </div>

      {dados.areas.map((a, ai) => (
        <div key={ai} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <input type="text" disabled={ro} value={a.nome}
              onChange={(e) => atualizarArea(ai, 'nome', e.target.value)}
              style={{ flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontWeight: 700 }} />
            {!ro && (
              <button onClick={() => removerArea(ai)} style={btn('#fee2e2', '#991b1b')} title="Remover área">
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {a.perguntas.map((p, pi) => (
              <div key={pi} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="text" disabled={ro} value={p.texto} placeholder="Texto da pergunta"
                  onChange={(e) => atualizarPergunta(ai, pi, 'texto', e.target.value)}
                  style={{ flex: 1, minWidth: 220, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                {/* Pontua: entra no denominador da nota. Crítica: "Não" veta (IMPUR). */}
                <label title="A pergunta entra no cálculo da nota (SIM ÷ SIM+NÃO)"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" disabled={ro} checked={!!p.pontuada}
                    onChange={(e) => atualizarPergunta(ai, pi, 'pontuada', e.target.checked)} />
                  Pontua
                </label>
                <label title='Questão crítica: respondê-la "Não" reprova a avaliação (IMPUR), independente da nota'
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#b91c1c', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" disabled={ro} checked={!!p.e_critica}
                    onChange={(e) => atualizarPergunta(ai, pi, 'e_critica', e.target.checked)} />
                  Crítica
                </label>
                {!ro && (
                  <button onClick={() => removerPergunta(ai, pi)} style={btn('#fee2e2', '#991b1b')} title="Remover">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
            {!ro && (
              <button onClick={() => adicionarPergunta(ai)} style={btn('#eff6ff', '#1d4ed8')}>
                <Plus size={12} /> Pergunta
              </button>
            )}
          </div>
        </div>
      ))}

      {!ro && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={adicionarArea} style={{
            padding: '10px 16px', background: '#f1f5f9', color: '#475569',
            border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
          }}>
            <Plus size={14} /> Área
          </button>
          <button onClick={salvar} disabled={salvando} style={{
            padding: '10px 20px', background: salvando ? '#94a3b8' : cor, color: '#fff',
            border: 'none', borderRadius: 8, fontWeight: 800, cursor: salvando ? 'wait' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <Save size={14} /> {salvando ? 'Salvando…' : 'Salvar estrutura'}
          </button>
        </div>
      )}
    </div>
  );
};

export default FormularioVersoes;
